import { useCallback, useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Knob } from "./Knob";
import { type KnobDataProps, knobDataSchema } from "./Knob.schema";
import { KnobControl } from "./KnobControl";
import { KnobIndicator } from "./KnobIndicator";
import { KnobLabel } from "./KnobLabel";
import { KnobThumb } from "./KnobThumb";
import { KnobTrack } from "./KnobTrack";
import { KnobValue } from "./KnobValue";
import { KNOB_TAG_NAME } from "./constants";

export interface KnobHandle {
  value: (value: number) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [KNOB_TAG_NAME]: { props: KnobDataProps; handle: KnobHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-knob": HTMLElement & KnobHandle;
  }
}

export function RWCKnob({
  ref,
  disabled: initialDisabled,
  value: initialValue,
  min,
  label,
  ...props
}: KnobDataProps & { ref?: Ref<KnobHandle> }) {
  const host = useIslandHost();

  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [value, setValue] = useState(initialValue ?? min ?? 0);

  const handle: KnobHandle = {
    value: setValue,
    disabled: (value = true) => setDisabled(value),
  };

  useImperativeHandle(ref, () => handle, []);

  const syncFormValue = useCallback(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : String(value));
  }, [host, disabled, value]);

  useEffect(syncFormValue, [syncFormValue]);

  return (
    <Knob
      {...props}
      min={min}
      value={value}
      disabled={disabled}
      onValueChange={setValue}
    >
      {label ? <KnobLabel>{label}</KnobLabel> : null}
      <KnobControl>
        <KnobTrack>
          <KnobIndicator />
        </KnobTrack>
        <KnobThumb />
        <KnobValue />
      </KnobControl>
    </Knob>
  );
}

componentManager.register(
  rwc(KNOB_TAG_NAME, {
    component: RWCKnob,
    dataSchema: knobDataSchema,
    formAssociated: true,
  }),
);
